import React, { useMemo } from "react";
import { Button, DatePicker, Dropdown, InputNumber, Table, Tag, Tooltip } from "antd";
import { DownOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { BACKEND_DATE_FORMAT, COLORS, UI_DATE_FORMAT } from "../../../../consts";
import {
  calculateRowDimensions,
  cleanGhName,
  hexToRgba,
  isDisplayedLeafSizeWithinRange,
  roundLeafSizeForDisplay,
} from "./utils";

const useSpinachDashboardTable = ({
  harvestDateEdits,
  setHarvestDateEdits,
  openHarvestPickers,
  setOpenHarvestPickers,
  yieldEdits,
  setYieldEdits,
  weightEdits,
  setWeightEdits,
  showYieldInput,
  setShowYieldInput,
  showWeightInput,
  setShowWeightInput,
  summaryForecastData,
  sortedForecastsCacheRef,
  rowGeometryCacheRef,
  naturalRowNameSort,
  siteMetadata,
  blockLabel,
  greenHouseLabel,
  minimumIdealHarvestLeafSize,
  maximumIdealHarvestLeafSize,
  yieldKeyNameForTable,
  weightKeyNameForTable,
  showForecastWeightColumn,
  hasPendingEdits,
  handleBulkSave,
  bulkSaving,
}) => {
  const getDotColor = (value) => {
    if (value === null || value === undefined) return COLORS.darkGray;
    if (value > 0) return COLORS.successGreen;
    if (value === 0) return COLORS.alertYellow;
    return COLORS.dangerRed; // -1
  };

  const StatusDot = ({ color }) => (
    <span
      className="inline-block rounded-full shrink-0"
      style={{ width: 8, height: 8, backgroundColor: color }}
    />
  );

  const highlightIfExceeds = (value) => {
    return (
      <span
        style={{
          // backgroundColor: "#ffd6da",
          backgroundColor: COLORS.alertYellow,
          padding: "2px 4px",
          borderRadius: "4px",
        }}
      >
        {value.toFixed(1)}
      </span>
    );
  };

  // Forecast renderers
  const forecastRenderer = useMemo(() => {
    return {
      currentLength: (_, record) => {
        const val = record.lastData?.averagePercentile80_95;
        const dateStr = record.lastData?.date;
        if (!val || typeof val !== "number") return "N/A";
        const displayVal =
          isDisplayedLeafSizeWithinRange(
            val,
            minimumIdealHarvestLeafSize,
            maximumIdealHarvestLeafSize,
          )
            ? highlightIfExceeds(val)
            : val.toFixed(1);

        return (
          <>
            {displayVal} cm
            {dateStr && (
              <span style={{ marginLeft: "0.5em" }}>({dateStr})</span>
            )}
          </>
        );
      },
      forecastCell: (forecastDayObj, record) => {
        if (typeof forecastDayObj === "string") return forecastDayObj;
        if (!forecastDayObj.date) return "N/A";

        const current = record.lastData?.averagePercentile80_95 || 0;
        const inc = forecastDayObj.averagePercentile80_95 || 0;
        const total = current + inc;

        const displayedVal =
          isDisplayedLeafSizeWithinRange(
            total,
            minimumIdealHarvestLeafSize,
            maximumIdealHarvestLeafSize,
          )
            ? highlightIfExceeds(total)
            : roundLeafSizeForDisplay(total);

        return (
          <>
            {displayedVal} ({inc.toFixed(1)}) cm ({forecastDayObj.date})
          </>
        );
      },
    };
  }, [minimumIdealHarvestLeafSize, maximumIdealHarvestLeafSize]);

  // Harvest date cell
  const renderHarvestDateCell = (text, record, greenhouseName) => {
    const rowKey = `${greenhouseName}-${record.plotName}`;
    const liveValue = harvestDateEdits[rowKey]?.value
      ? harvestDateEdits[rowKey].value
      : record.harvestDate
        ? dayjs(record.harvestDate, BACKEND_DATE_FORMAT)
        : null;

    const handleChange = (date) => {
      setHarvestDateEdits((prev) => ({
        ...prev,
        [rowKey]: { value: date },
      }));
    };
    const isOpen = !!openHarvestPickers[rowKey];
    return (
      <div className="flex items-center gap-2">
        {!isOpen && (
          <>
            <span className="w-24">
              {liveValue?.isValid?.()
                ? liveValue.format(UI_DATE_FORMAT)
                : "Unharvested"}
            </span>
            <Button
              size="small"
              onClick={() =>
                setOpenHarvestPickers((p) => ({ ...p, [rowKey]: true }))
              }
            >
              <EditOutlined></EditOutlined>
            </Button>
          </>
        )}

        {isOpen && (
          <DatePicker
            autoFocus
            open
            placeholder="Select Date"
            value={liveValue ?? null}
            format={UI_DATE_FORMAT}
            allowClear={false}
            onChange={(d) => handleChange(d)}
            onOpenChange={(open) => {
              if (!open) {
                setOpenHarvestPickers((p) => ({ ...p, [rowKey]: false }));
              }
            }}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        )}
      </div>
    );
  };

  const renderYieldCell = (record, greenhouseName) => {
    const rowKey = `${greenhouseName}-${record.plotName}`;
    const liveHarvest = harvestDateEdits[rowKey]?.value
      ? harvestDateEdits[rowKey].value
      : record.harvestDate
        ? dayjs(record.harvestDate, BACKEND_DATE_FORMAT)
        : null;

    const liveValue =
      yieldEdits[rowKey]?.value !== undefined
        ? yieldEdits[rowKey].value
        : record.yield;

    const isYieldInputVisible = showYieldInput[rowKey] || false;

    const handleChange = (value) => {
      setYieldEdits((prev) => ({ ...prev, [rowKey]: { value } }));
    };

    const handleAddYield = () => {
      setShowYieldInput((prev) => ({ ...prev, [rowKey]: true }));
      if (liveValue <= 0 || liveValue === undefined || liveValue === null) {
        handleChange(1);
      }
    };

    // Always allow Discarded; block other actions until date exists
    const quickSelectOptions = [
      {
        key: "discarded",
        label: (
          <div className="flex items-center gap-2">
            <StatusDot color={COLORS.dangerRed} />
            <span>Discarded</span>
          </div>
        ),
        disabled: false,
        onClick: () => {
          handleChange(-1);
          setShowYieldInput((p) => ({ ...p, [rowKey]: false }));
        },
      },
      {
        key: "unavailable",
        label: (
          <div className="flex items-center gap-2">
            <StatusDot color={COLORS.alertYellow} />
            <span>Unavailable</span>
          </div>
        ),
        disabled: !liveHarvest,
        onClick: () => {
          handleChange(0);
          setShowYieldInput((p) => ({ ...p, [rowKey]: false }));
        },
      },
      {
        key: "addYield",
        label: (
          <div className="flex items-center gap-2">
            <StatusDot color={COLORS.successGreen} />
            <span>Add {yieldKeyNameForTable}</span>
          </div>
        ),
        disabled: !liveHarvest,
        onClick: handleAddYield,
      },
    ];

    const baseBoxClasses =
      "w-25 shrink-0 px-3 py-1.5 min-h-[36px] rounded-md border-2 shadow-sm hover:shadow-md transition-all duration-200";

    const getStyleForValue = (v) => {
      if (v > 0) {
        return {
          backgroundColor: hexToRgba(COLORS.successGreen, 0.1),
          borderColor: COLORS.successGreen,
        };
      } else if (v === 0) {
        return {
          backgroundColor: hexToRgba(COLORS.alertYellow, 0.1),
          borderColor: COLORS.alertYellow,
        };
      } else if (v === -1) {
        return {
          backgroundColor: hexToRgba(COLORS.dangerRed, 0.1),
          borderColor: COLORS.dangerRed,
        };
      }
      return {
        backgroundColor: hexToRgba(COLORS.darkGray, 0.1),
        borderColor: COLORS.darkGray,
      };
    };

    const shouldForceInput =
      !!liveHarvest && (liveValue === null || liveValue === undefined);

    const baseBoxStyle = {
      ...(shouldForceInput
        ? {
            backgroundColor: hexToRgba(COLORS.successGreen, 0.1),
            borderColor: COLORS.successGreen,
          }
        : getStyleForValue(liveValue)),
      opacity: !liveHarvest ? 0.9 : 1,
    };

    const isSpecialValue = liveValue === 0 || liveValue === -1;
    const pillText =
      liveValue === 0
        ? "Unavailable"
        : liveValue === -1
          ? "Discarded"
          : String(liveValue);

    return (
      <div className="relative flex items-center gap-2">
        <span
          className="inline-block rounded-full shrink-0"
          style={{
            width: 8,
            height: 8,
            backgroundColor: getDotColor(liveValue),
          }}
        />

        {isSpecialValue && !isYieldInputVisible && !shouldForceInput ? (
          <div className={baseBoxClasses} style={baseBoxStyle}>
            <span className="leading-none">{pillText}</span>
          </div>
        ) : (
          <InputNumber
            value={liveValue ?? undefined}
            onChange={handleChange}
            min={-1}
            step={1}
            // keep numeric edits blocked until a date, but "Discarded" can still be chosen via menu
            disabled={!liveHarvest}
            placeholder={"Enter " + yieldKeyNameForTable}
            className="!w-25 !min-h-[36px] !rounded-md !border-2 shrink-0 shadow-sm hover:shadow-md focus:!outline-none focus:!ring-0"
            style={baseBoxStyle}
            onBlur={(e) => {
              const val = parseFloat(e.target.value);
              if (val === 0 || val === -1) {
                handleChange(val);
                setShowYieldInput((p) => ({ ...p, [rowKey]: false }));
              }
            }}
          />
        )}

        {/* Always allow the menu so "Discarded" can be set without a date */}
        <Dropdown menu={{ items: quickSelectOptions }} trigger={["click"]}>
          <button
            className="h-8 w-8 rounded-full border-2 bg-white flex items-center justify-center hover:shadow-md transition"
            style={{ borderColor: baseBoxStyle.borderColor }}
            title="Options"
          >
            <DownOutlined className="opacity-70" />
          </button>
        </Dropdown>
      </div>
    );
  };

  const renderWeightCell = (record, greenhouseName) => {
    const rowKey = `${greenhouseName}-${record.plotName}`;
    const liveHarvest = harvestDateEdits[rowKey]?.value
      ? harvestDateEdits[rowKey].value
      : record.harvestDate
        ? dayjs(record.harvestDate, BACKEND_DATE_FORMAT)
        : null;

    const liveValue =
      weightEdits[rowKey]?.value !== undefined
        ? weightEdits[rowKey].value
        : record.weight;

    const isWeightInputVisible = showWeightInput[rowKey] || false;

    const handleChange = (value) => {
      setWeightEdits((prev) => ({ ...prev, [rowKey]: { value } }));
    };

    const handleAddWeight = () => {
      setShowWeightInput((prev) => ({ ...prev, [rowKey]: true }));
      if (liveValue <= 0 || liveValue === undefined || liveValue === null) {
        handleChange(1);
      }
    };

    const quickSelectOptions = [
      {
        key: "unavailable",
        label: (
          <div className="flex items-center gap-2">
            <StatusDot color={COLORS.alertYellow} />
            <span>Unavailable</span>
          </div>
        ),
        onClick: () => {
          handleChange(0);
          setShowWeightInput((p) => ({ ...p, [rowKey]: false }));
        },
      },
      {
        key: "discarded",
        label: (
          <div className="flex items-center gap-2">
            <StatusDot color={COLORS.dangerRed} />
            <span>Discarded</span>
          </div>
        ),
        onClick: () => {
          handleChange(-1);
          setShowWeightInput((p) => ({ ...p, [rowKey]: false }));
        },
      },
      {
        key: "addWeight",
        label: (
          <div className="flex items-center gap-2">
            <StatusDot color={COLORS.successGreen} />
            <span>Add {weightKeyNameForTable}</span>
          </div>
        ),
        onClick: handleAddWeight,
      },
    ];

    const baseBoxClasses =
      "w-25 shrink-0 px-3 py-1.5 min-h-[36px] rounded-md border-2 shadow-sm hover:shadow-md transition-all duration-200";

    const getStyleForValue = (v) => {
      if (v > 0) {
        return {
          backgroundColor: hexToRgba(COLORS.successGreen, 0.1),
          borderColor: COLORS.successGreen,
        };
      } else if (v === 0) {
        return {
          backgroundColor: hexToRgba(COLORS.alertYellow, 0.1),
          borderColor: COLORS.alertYellow,
        };
      } else if (v === -1) {
        return {
          backgroundColor: hexToRgba(COLORS.dangerRed, 0.1),
          borderColor: COLORS.dangerRed,
        };
      }
      return {
        backgroundColor: hexToRgba(COLORS.darkGray, 0.1),
        borderColor: COLORS.darkGray,
      };
    };

    const shouldForceInput =
      !!liveHarvest && (liveValue === null || liveValue === undefined);

    const baseBoxStyle = {
      ...(shouldForceInput
        ? {
            backgroundColor: hexToRgba(COLORS.successGreen, 0.1),
            borderColor: COLORS.successGreen,
          }
        : getStyleForValue(liveValue)),
      opacity: !liveHarvest ? 0.6 : 1,
    };

    const isSpecialValue = liveValue === 0 || liveValue === -1;
    const pillText =
      liveValue === 0
        ? "Unavailable"
        : liveValue === -1
          ? "Discarded"
          : String(liveValue);

    return (
      <div className="relative flex items-center gap-2">
        <span
          className="inline-block rounded-full shrink-0"
          style={{
            width: 8,
            height: 8,
            backgroundColor: getDotColor(liveValue ?? 1),
          }}
        />

        {isSpecialValue && !isWeightInputVisible && !shouldForceInput ? (
          <div className={baseBoxClasses} style={baseBoxStyle}>
            <span className="leading-none">{pillText}</span>
          </div>
        ) : (
          <InputNumber
            value={liveValue ?? undefined}
            onChange={handleChange}
            min={-1}
            step={1}
            disabled={!liveHarvest}
            placeholder={"Enter " + weightKeyNameForTable}
            className="!w-25 !min-h-[36px] !rounded-md !border-2 shrink-0 shadow-sm hover:shadow-md focus:!outline-none focus:!ring-0"
            style={baseBoxStyle}
            onBlur={(e) => {
              const val = parseFloat(e.target.value);
              if (val === 0 || val === -1) {
                handleChange(val);
                setShowWeightInput((p) => ({ ...p, [rowKey]: false }));
              }
            }}
          />
        )}
        {liveHarvest && (
          <Dropdown
            menu={{ items: quickSelectOptions }}
            trigger={["click"]}
            disabled={!liveHarvest}
          >
            <button
              className="h-8 w-8 rounded-full border-2 bg-white flex items-center justify-center hover:shadow-md transition"
              style={{ borderColor: baseBoxStyle.borderColor }}
              title="Options"
            >
              <DownOutlined className="opacity-70" />
            </button>
          </Dropdown>
        )}
      </div>
    );
  };

  const renderWithinRecommendedCell = (record, greenhouseName) => {
    const rowKey = `${greenhouseName}-${record.plotName}`;
    const liveHarvest = harvestDateEdits[rowKey]?.value
      ? harvestDateEdits[rowKey].value
      : record.harvestDate
        ? dayjs(record.harvestDate, BACKEND_DATE_FORMAT)
        : null;

    // 1) No harvest date -> N/A
    if (!liveHarvest || !liveHarvest.isValid?.()) {
      return (
        <Tag color={COLORS.darkerGray} variant={"outlined"}>
          <strong>N/A</strong>
        </Tag>
      );
    }

    const harvestDateBackend = liveHarvest.format(BACKEND_DATE_FORMAT);
    const min = minimumIdealHarvestLeafSize;
    const max = maximumIdealHarvestLeafSize;

    const inRange = (v) => isDisplayedLeafSizeWithinRange(v, min, max);

    // Check against the last measured date
    if (record?.lastData?.date) {
      const lastBackend = dayjs(record.lastData.date, UI_DATE_FORMAT).format(
        BACKEND_DATE_FORMAT,
      );
      if (lastBackend === harvestDateBackend) {
        const ok = inRange(record.lastData.averagePercentile80_95);
        return (
          <Tag
            color={ok ? COLORS.successGreen : COLORS.dangerRed}
            variant={"outlined"}
          >
            <strong>{ok ? "Yes" : "No"}</strong>
          </Tag>
        );
      }
    }

    // Check against Day 1..5 forecasts (current + increment)
    const current = record?.lastData?.averagePercentile80_95 || 0;
    for (const key of ["day1", "day2", "day3", "day4", "day5"]) {
      const d = record[key];
      if (d && typeof d === "object" && d.date) {
        const fBackend = dayjs(d.date, UI_DATE_FORMAT).format(
          BACKEND_DATE_FORMAT,
        );
        if (fBackend === harvestDateBackend) {
          const total = current + (d.averagePercentile80_95 || 0);
          const ok = inRange(total);
          return (
            <Tag
              color={ok ? COLORS.successGreen : COLORS.dangerRed}
              variant={"outlined"}
            >
              <strong>{ok ? "Yes" : "No"}</strong>
            </Tag>
          );
        }
      }
    }

    // 3) Harvest date present but not matching any forecast date => No
    return (
      <Tag color={COLORS.dangerRed} variant={"outlined"}>
        <strong>No</strong>
      </Tag>
    );
  };

  // -----------------------------------
  // Table for greenhouse
  // -----------------------------------
  const renderGreenhouseTable = (ghName) => {
    const sourceForecasts = summaryForecastData[ghName] || [];
    const cached = sortedForecastsCacheRef.current.get(ghName);
    const rowForecasts =
      cached && cached.source === sourceForecasts
        ? cached.sorted
        : (() => {
            const sorted = [...sourceForecasts].sort((a, b) =>
              naturalRowNameSort(a.plotName, b.plotName),
            );
            sortedForecastsCacheRef.current.set(ghName, {
              source: sourceForecasts,
              sorted,
            });
            return sorted;
          })();
    if (rowForecasts.length === 0) return null;

    const yieldPerMeterTitle =
      siteMetadata?.FORECAST_GREENHOUSE_TABLE?.YIELD_PER_METER_TITLE ||
      "Yield Per Meter";

    const yieldPerHectareTitle =
      siteMetadata?.FORECAST_GREENHOUSE_TABLE?.YIELD_PER_TON_HECTARE_TITLE ||
      "Yield Per Hectare (ton/ha)";
    const minYieldPerMeter =
      siteMetadata?.FORECAST_GREENHOUSE_TABLE
        ?.MINIMUM_IDEAL_VALUE_YIELD_PER_METER || 1;
    const maxYieldPerMeter =
      siteMetadata?.FORECAST_GREENHOUSE_TABLE
        ?.MAXIMUM_IDEAL_VALUE_YIELD_PER_METER || 3;
    const yieldPerTubs =
      siteMetadata?.FORECAST_GREENHOUSE_TABLE?.YIELD_PER_TUBS || 1;

    /**
     * Harvested weight of a row, in kg.
     *
     * Prefer the weight the grower actually recorded. Frais enters a per-row
     * weight next to the crate count, and deriving the yield from
     * `count * YIELD_PER_TUBS` ignored it: 32 crates * 2 kg = 64 kg against the
     * 194 kg weighed, understating yield/metre threefold. Sites that record no
     * weight (Boratto counts tubs) fall back to the count, unchanged.
     */
    const resolveTotalYield = (record) => {
      const weight = Number(record?.weight);
      if (Number.isFinite(weight) && weight > 0) return weight;
      return (Number(record?.yield) || 0) * yieldPerTubs;
    };

    const getRowGeometry = (plotName) => {
      const key = `${ghName}|${plotName}`;
      const cache = rowGeometryCacheRef.current;
      if (cache.has(key)) return cache.get(key);

      const name = cleanGhName(ghName);
      const rowCoordinates =
        siteMetadata?.GREENHOUSE_COORDINATES?.[name]?.[plotName];
      if (
        !rowCoordinates ||
        !Array.isArray(rowCoordinates) ||
        rowCoordinates.length < 3
      ) {
        cache.set(key, null);
        return null;
      }

      const { length, width } = calculateRowDimensions(rowCoordinates);
      const value = { length, width };
      cache.set(key, value);
      return value;
    };

    const renderYieldPerMeter = (_, record) => {
      const geometry = getRowGeometry(record.plotName);
      if (!geometry || geometry.length <= 0) {
        return "N/A";
      }

      // If the row is not harvested or has no yield, return N/A
      const totalYield = resolveTotalYield(record);
      if (totalYield <= 0) {
        return "N/A";
      }

      // Calculate yield per meter
      const yieldPerMeter = totalYield / geometry.length;
      const formattedValue = yieldPerMeter.toFixed(1);

      // Determine color based on value
      let color;
      if (formattedValue < minYieldPerMeter) {
        color = COLORS.dangerRed;
      } else if (formattedValue > maxYieldPerMeter) {
        color = COLORS.successGreen;
      } else {
        color = COLORS.primaryBlue;
      }

      const tooltipContent = (
        <div>
          <p>
            {blockLabel} Length: {geometry.length.toFixed(1)} meters
          </p>
          <p>Total Yield: {totalYield.toFixed(1)} kg</p>
          <p>Yield Per Meter: {formattedValue} kg/m</p>
          <p className="mt-2 text-xs">
            {formattedValue < minYieldPerMeter
              ? "Below minimum target yield"
              : formattedValue > maxYieldPerMeter
                ? "Above maximum target yield"
                : "Within target yield range"}
          </p>
        </div>
      );

      return (
        <Tooltip
          title={tooltipContent}
          placement="left"
          styles={{
            root: {
              maxWidth: "300px",
            },
            container: {
              backgroundColor: hexToRgba(color, 0.95),
              color: "white",
              borderRadius: "6px",
              boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
            },
          }}
        >
          <Tag
            color={color}
            className="font-medium"
            style={{
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            {formattedValue} Kg/m
          </Tag>
        </Tooltip>
      );
    };
    const renderYieldPerHectare = (_, record) => {
      const geometry = getRowGeometry(record.plotName);
      if (!geometry || geometry.length <= 0 || geometry.width <= 0) {
        return "N/A";
      }

      // If the row is not harvested or has no yield, return N/A
      const totalYield = resolveTotalYield(record);
      if (totalYield <= 0) {
        return "N/A";
      }

      const areaInSqMeters = geometry.length * geometry.width;
      const yieldPerSqMeter = totalYield / areaInSqMeters;
      const yieldPerTonHectare = yieldPerSqMeter * 10; // Convert to ton per hectare
      const formattedValue = yieldPerTonHectare.toFixed(2);

      const tooltipContent = (
        <div>
          <p>
            {blockLabel} Length: {geometry.length.toFixed(1)} meters
          </p>
          <p>
            {blockLabel} Width: {geometry.width.toFixed(1)} meters
          </p>
          <p>Total Yield: {totalYield.toFixed(1)} kg</p>
          <p>Yield per m² : {yieldPerSqMeter.toFixed(2)} kg/m²</p>
          <p>
            {yieldPerHectareTitle}: {formattedValue} ton/ha
          </p>
        </div>
      );

      return (
        <Tooltip
          title={tooltipContent}
          placement="left"
          styles={{
            root: {
              maxWidth: "300px",
            },
            container: {
              backgroundColor: hexToRgba(COLORS.primaryBlue, 0.95),
              color: "white",
              borderRadius: "6px",
              boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
            },
          }}
        >
          <span className="font-bold cursor-pointer">
            {formattedValue} ton/Ha
          </span>
        </Tooltip>
      );
    };

    const columns = [
      {
        title: "No.",
        dataIndex: "plotName",
        key: "plotName",
        fixed: "left",
        width: 60,
        sorter: (a, b) => naturalRowNameSort(a.plotName, b.plotName),
        sortDirections: ["ascend", "descend"],
        className: "!w-[80px]",
      },
      {
        title: "Current Length (cm)",
        key: "currentLength",
        render: forecastRenderer.currentLength,
        sorter: (a, b) =>
          a.lastData.averagePercentile80_95 - b.lastData.averagePercentile80_95,
        className: "lg:w-auto w-[120px]",
        onHeaderCell: () => ({
          style: {
            minWidth: 120,
            maxWidth: 120,
          },
        }),
        onCell: () => ({
          style: {
            minWidth: 120,
            maxWidth: 120,
            whiteSpace: "pre-wrap",
          },
        }),
      },
      ...["day1", "day2", "day3", "day4", "day5"].map((day, index) => ({
        title: `Forecast: Day ${index + 1}`,
        key: day,
        render: (_, record) =>
          forecastRenderer.forecastCell(record[day], record),
        sorter: (a, b) =>
          (a[day].averagePercentile80_95 || 0) -
          (b[day].averagePercentile80_95 || 0),
        className: "lg:w-auto w-[120px]",
        onHeaderCell: () => ({
          style: {
            minWidth: 120,
            maxWidth: 120,
          },
        }),
        onCell: () => ({
          style: {
            minWidth: 120,
            maxWidth: 120,
            whiteSpace: "pre-wrap",
          },
        }),
      })),
      {
        title: "Harvest Date",
        dataIndex: "harvestDate",
        key: "harvestDate",
        render: (text, record) => renderHarvestDateCell(text, record, ghName),
        className: "lg:w-auto w-[160px]",
        onHeaderCell: () => ({
          style: {
            minWidth: 160,
            maxWidth: 160,
          },
        }),
        onCell: () => ({
          style: {
            minWidth: 160,
            maxWidth: 160,
            whiteSpace: "pre-wrap",
          },
        }),
      },
      {
        title: yieldKeyNameForTable,
        key: "yield",
        render: (_, record) => renderYieldCell(record, ghName),
        className: "lg:w-auto w-[150px]",
        onHeaderCell: () => ({
          style: {
            minWidth: 150,
            maxWidth: 180,
          },
        }),
        onCell: () => ({
          style: {
            minWidth: 150,
            maxWidth: 180,
            whiteSpace: "nowrap",
          },
        }),
      },
      ...(showForecastWeightColumn
        ? [
            {
              title: weightKeyNameForTable,
              key: "weight",
              render: (_, record) => renderWeightCell(record, ghName),
              className: "lg:w-auto w-[150px]",
              onHeaderCell: () => ({
                style: {
                  minWidth: 150,
                  maxWidth: 180,
                },
              }),
              onCell: () => ({
                style: {
                  minWidth: 150,
                  maxWidth: 180,
                  whiteSpace: "nowrap",
                },
              }),
            },
          ]
        : []),
      {
        title: "Within Recommendation",
        key: "withinRecommended",
        render: (_, record) => renderWithinRecommendedCell(record, ghName),
        className: "lg:w-auto w-[130px]",
        onHeaderCell: () => ({
          style: {
            minWidth: 130,
            maxWidth: 130,
          },
        }),
        onCell: () => ({
          style: {
            minWidth: 130,
            maxWidth: 130,
            whiteSpace: "nowrap",
            textAlign: "center",
          },
        }),
      },
      {
        title: yieldPerMeterTitle,
        key: "yieldPerMeter",
        render: renderYieldPerMeter,
        sorter: (a, b) => {
          // Calculate yield per meter for both records
          const calcYieldPerMeter = (record) => {
            const geometry = getRowGeometry(record.plotName);
            if (!geometry || geometry.length <= 0) return -1;
            const totalYield = resolveTotalYield(record);
            if (totalYield <= 0) {
              return -1; // No yield data
            }

            return totalYield / geometry.length;
          };

          const ypmA = calcYieldPerMeter(a);
          const ypmB = calcYieldPerMeter(b);

          // Handle cases where one or both values are invalid
          if (ypmA === -1 && ypmB === -1) return 0;
          if (ypmA === -1) return -1;
          if (ypmB === -1) return 1;

          return ypmA - ypmB;
        },
        className: "lg:w-auto w-[130px]",
        onHeaderCell: () => ({
          style: {
            minWidth: 130,
            maxWidth: 130,
          },
        }),
        onCell: () => ({
          style: {
            minWidth: 130,
            maxWidth: 130,
            whiteSpace: "nowrap",
            textAlign: "center",
          },
        }),
      },
      {
        title: yieldPerHectareTitle,
        key: "yieldPerTonHectare",
        render: renderYieldPerHectare,
        sorter: (a, b) => {
          // Calculate yield per meter for both records
          const calcYieldPerTonHectare = (record) => {
            const geometry = getRowGeometry(record.plotName);
            if (!geometry || geometry.length <= 0 || geometry.width <= 0) {
              return -1;
            }
            const totalYield = resolveTotalYield(record);
            if (totalYield <= 0) {
              return -1; // No yield data
            }

            const areaInSqMeters = geometry.length * geometry.width;
            const yieldPerSqMeter = totalYield / areaInSqMeters;
            return yieldPerSqMeter * 10;
          };

          const ypmA = calcYieldPerTonHectare(a);
          const ypmB = calcYieldPerTonHectare(b);

          // Handle cases where one or both values are invalid
          if (ypmA === -1 && ypmB === -1) return 0;
          if (ypmA === -1) return -1;
          if (ypmB === -1) return 1;

          return ypmA - ypmB;
        },
        className: "lg:w-auto w-[130px]",
        onHeaderCell: () => ({
          style: {
            minWidth: 130,
            maxWidth: 130,
          },
        }),
        onCell: () => ({
          style: {
            minWidth: 130,
            maxWidth: 130,
            whiteSpace: "nowrap",
            textAlign: "center",
          },
        }),
      },
    ];

    return (
      <div key={ghName} className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h2 className="text-xl font-bold">
              {greenHouseLabel}: {ghName}
            </h2>
            {/* Add yield per meter summary */}
            {(() => {
              // Calculate summary
              const harvestedRows = rowForecasts.filter((row) => row.yield > 0);
              if (harvestedRows.length === 0) return null;

              let totalYieldPerMeter = 0;
              let validRowCount = 0;

              harvestedRows.forEach((row) => {
                const geometry = getRowGeometry(row.plotName);
                if (!geometry || geometry.length <= 0) return;

                const totalYield = resolveTotalYield(row);
                if (totalYield <= 0) return;
                const yieldPerMeter = totalYield / geometry.length;

                totalYieldPerMeter += yieldPerMeter;
                validRowCount++;
              });

              if (validRowCount === 0) return null;
              const averageYieldPerMeter = totalYieldPerMeter / validRowCount;

              // Determine color
              let color;
              if (averageYieldPerMeter < minYieldPerMeter) {
                color = COLORS.dangerRed;
              } else if (averageYieldPerMeter > maxYieldPerMeter) {
                color = COLORS.successGreen;
              } else {
                color = COLORS.primaryBlue;
              }

              return (
                <div className="text-sm mt-1">
                  <span className="mr-2">Average {yieldPerMeterTitle}:</span>

                  <Tag
                    color={color}
                    className="font-medium"
                    style={{
                      padding: "1px 6px",
                      borderRadius: "4px",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    {averageYieldPerMeter.toFixed(1)} Kg/m
                  </Tag>

                  <span className="ml-2 text-gray-500">
                    ({validRowCount} of {rowForecasts.length} {blockLabel}s)
                  </span>
                </div>
              );
            })()}
          </div>
          <Button
            type="primary"
            loading={bulkSaving[ghName]}
            disabled={!hasPendingEdits(ghName)}
            onClick={() => handleBulkSave(ghName)}
          >
            Save
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={rowForecasts}
          rowKey={(record) => record.plotName}
          pagination={false}
          scroll={{ x: "max-content" }}
        />
      </div>
    );
  };

  return { renderGreenhouseTable };
};

export default useSpinachDashboardTable;
